import React from 'react';
import { Typography, Box } from '@mui/material';

interface HealthCardProps {
  title: string;
  value: number | string;
  unit?: string;
  bgColor: string;
  textColor?: string;
  /** Recent trend of this metric's own value (one point per update), oldest first.
   *  Drawn as a small sparkline under the number — not the raw physiological
   *  waveform (PPG/ECG/etc.), just this stat's history over time. */
  history?: number[];
}

// Minimal inline sparkline — no chart library needed for a handful of points.
// Flat/degenerate ranges (all-equal or <2 points) draw a flat mid-line instead
// of a NaN-producing division by zero.
const Sparkline: React.FC<{ data: number[]; color: string }> = ({ data, color }) => {
  const W = 100, H = 28, PAD = 3;
  const valid = data.filter((v) => Number.isFinite(v));
  if (valid.length < 2) return null;

  const min = Math.min(...valid);
  const max = Math.max(...valid);
  const range = max - min;
  const points = valid.map((v, i) => {
    const x = (i / (valid.length - 1)) * (W - PAD * 2) + PAD;
    const y = range > 0 ? H - PAD - ((v - min) / range) * (H - PAD * 2) : H / 2;
    return [x, y];
  });

  const linePath = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L${points[points.length - 1][0].toFixed(1)},${H} L${points[0][0].toFixed(1)},${H} Z`;
  const [lastX, lastY] = points[points.length - 1];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" style={{ display: 'block' }}>
      <path d={areaPath} fill={color} opacity={0.16} />
      <path d={linePath} fill="none" stroke={color} strokeWidth={1.75} strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={lastX} cy={lastY} r={2} fill={color} />
    </svg>
  );
};

const HealthCard: React.FC<HealthCardProps> = ({
  title,
  value,
  unit,
  bgColor,
  textColor = 'rgba(255,255,255,0.8)',
  history,
}) => {
  return (
    <Box sx={{ textAlign: 'center', backgroundColor: bgColor, borderRadius: '12px', py: { xs: 1.2, md: 2 }, px: { xs: 0.5, md: 1 }, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.5 }}>
      <Typography sx={{ fontSize: { xs: '0.52rem', md: '0.6rem' }, fontWeight: 700, color: 'rgba(255,255,255,0.4)', letterSpacing: '1.5px', textTransform: 'uppercase', lineHeight: 1.2 }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: { xs: '1.3rem', md: '1.9rem', lg: '2.1rem' }, fontWeight: 800, color: textColor, lineHeight: 1 }}>
        {value}
      </Typography>
      {unit && (
        <Typography sx={{ fontSize: { xs: '0.6rem', md: '0.72rem' }, fontWeight: 500, color: textColor, opacity: 0.6 }}>
          {unit}
        </Typography>
      )}
      {history && history.length >= 2 && (
        <Box sx={{ mt: 0.25, px: 0.5 }}>
          <Sparkline data={history} color={textColor} />
        </Box>
      )}
    </Box>
  );
};

export default HealthCard;
